import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  OwlDateTimeModule,
  OwlNativeDateTimeModule,
} from '@danielmoncada/angular-datetime-picker';
import { PpHttpConfig, PpHttpModule } from '@penpencil/common';
import { EditorModule, TINYMCE_SCRIPT_SRC } from '@tinymce/tinymce-angular';
import { MatSelectInfiniteScrollModule } from 'ng-mat-select-infinite-scroll';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MaterialFileInputModule } from 'ngx-material-file-input';

import { PpButtonComponent } from './components/button/button.component';
import { PpIconComponent } from './components/icon/icon.component';
import { PpLoaderComponent } from './components/loader/loader.component';
import { PpModalComponent } from './components/modal/modal.component';
import { PreviewDialogComponent } from './components/preview-dialog/preview-dialog.component';
import { PreviewComponent } from './components/preview/preview.component';
import { IgnoreDirtyDirective } from './directives/ignore-dirty.directive';
import { RemoveAriaOwnsDirective } from './directives/remove-aria-owns.directive';
import { MathJaxDirective } from './directives/mathsjax.directive';
import { PpFormComponent } from './forms.component';
import { PEN_PENCIL_FORMS_API_CONFIG } from './forms.constant';
import { PpFilterListPipe } from './pipes/filter-list.pipe';
import { PpConditionValidationService } from './services/condition-validation.service';
import { PpNotificationService } from './services/notification.service';
import { SafePipe } from './pipes/safe.pipe';

// Expose tinymce instance to the window
declare const require: any;
(window as any).tinymce = require('tinymce');

// import synchronous mathtype-tinymce5 package
require('@wiris/mathtype-tinymce5');

@NgModule({
  declarations: [
    PpFormComponent,
    PpButtonComponent,
    PpIconComponent,
    PpFilterListPipe,
    PpLoaderComponent,
    PpModalComponent,
    PreviewComponent,
    IgnoreDirtyDirective,
    RemoveAriaOwnsDirective,
    MathJaxDirective,
    PreviewDialogComponent,
    SafePipe,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialFileInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatRadioModule,
    MatCheckboxModule,
    MatBadgeModule,
    MatSlideToggleModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatSelectInfiniteScrollModule,
    NgxMatSelectSearchModule,
    MatIconModule,
    MatChipsModule,
    MatTooltipModule,
    MatButtonModule,
    MatSnackBarModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    EditorModule,
    // Owl Date Time Picker
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
    PpHttpModule.forRoot(),
  ],
  exports: [
    PpFormComponent,
    PpButtonComponent,
    PpIconComponent,
    PpFilterListPipe,
    PpLoaderComponent,
    PreviewComponent,
    IgnoreDirtyDirective,
    MathJaxDirective,
  ],
  entryComponents: [PpModalComponent, PreviewComponent],
})
export class PpFormsModule {
  static forRoot(config: PpHttpConfig): ModuleWithProviders<PpFormsModule> {
    return {
      ngModule: PpFormsModule,
      providers: [
        { provide: PEN_PENCIL_FORMS_API_CONFIG, useValue: config },
        { provide: TINYMCE_SCRIPT_SRC, useValue: 'tinymce/tinymce.min.js' },
        PpNotificationService,
        PpConditionValidationService,
      ],
    };
  }
}
